<template>
  <DialogFormWrapper hide-actions>
    <template #form>
      <div class="mt-2 mb-2 black--text">
        Zarządzaj blokadami: <b>{{ clientName }}</b>
      </div>
      <template v-if="mode === 'create'">
        <v-btn
          color="primary"
          small
          class="base-hover"
          @click="setCreateBlockage(false)"
        >
          Wróć
        </v-btn>
        <v-row class="mt-8">
          <v-col>
            <v-autocomplete
              v-model="blockType"
              :items="avaliableBlockages"
              label="Powód blokady"
              item-text="label"
              item-value="value"
              placeholder="Wybierz powód blokady"
              outlined
              dense
            />
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="12">
            <v-textarea
              outlined
              :rows="3"
              auto-grow
              label="Uzasadnienie"
              v-model="blockExplanation"
              placeholder="Wpisz powód nałożenia blokady"
            />
          </v-col>
        </v-row>
        <v-btn
          color="primary"
          class="base-hover"
          :loading="isProcessing"
          :disabled="!avaliableBlockages.length"
          v-shortkey="{ enter: ['enter'] }"
          @shortkey.native="createBlockage()"
          @click="createBlockage()"
        >
          Dodaj blokadę
        </v-btn>
      </template>
      <template v-else-if="mode === 'edit'">
        <v-btn
          color="primary"
          small
          class="base-hover"
          @click="setCreateBlockage(false)"
        >
          Wróć
        </v-btn>
        <v-row class="mt-8">
          <v-col>
            <v-autocomplete
              v-model="editingBlockage.blockageType"
              :items="allBlockages"
              label="Powód blokady"
              item-text="label"
              item-value="value"
              placeholder="Wybierz powód blokady"
              outlined
              disabled
              dense
            />
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="12">
            <v-textarea
              v-model="editingBlockage.explanation"
              outlined
              :rows="3"
              auto-grow
              label="Uzasadnienie"
              placeholder="Wpisz powód nałożenia blokady"
            />
          </v-col>
        </v-row>
        <v-btn
          color="primary"
          class="base-hover"
          :loading="isProcessing"
          v-shortkey="{ enter: ['enter'] }"
          @shortkey.native="editBlockage()"
          @click="editBlockage()"
        >
          Edytuj blokadę
        </v-btn>
      </template>
      <template v-else>
        <v-btn
          color="primary"
          small
          class="base-hover"
          :disabled="!avaliableBlockages.length"
          @click="setCreateBlockage()"
        >
          Dodaj blokadę
        </v-btn>
        <v-data-table
          :items="clientBlockages"
          :headers="headers"
          :items-per-page="-1"
          hide-default-footer
          fixed-header
          class="mt-4"
        >
          <template #[`item.blockageType`]="{ item }">
            {{ getBlockageTypeWithIcon(item.blockageType) }}
          </template>
          <template #[`item.blockedAt`]="{ item }">
            {{ item.blockedAt ? dateStringFormat(item.blockedAt) : '-' }}
          </template>
          <template #[`item.blockingUser.fullName`]="{ item }">
            {{ getBLockingUser(item) }}
          </template>
          <template #[`item.explanation`]="{ item }">
            {{ getTruncatedText(item.explanation, 150) }}
          </template>
          <template #[`item.action`]="{ item }">
            <div class="d-flex gap-1">
              <v-btn
                color="primary"
                class="mx-2"
                small
                icon
                @click="setEditingBlockage(item)"
              >
                <v-icon color="primary">
                  mdi-pencil
                </v-icon>
              </v-btn>
              <v-btn
                color="primary"
                class="mx-2"
                small
                icon
                @click="deleteBlockage(item.id)"
              >
                <v-icon color="error">
                  mdi-delete
                </v-icon>
              </v-btn>
            </div>
          </template>
        </v-data-table>
      </template>
    </template>
  </DialogFormWrapper>
</template>

<script>
import DialogFormWrapper from './Partials/DialogFormWrapper'
import { mapState, mapActions } from 'vuex'
import { getBlockageTypesOptions, dateStringFormat, getBlockageTypeWithIcon, getTruncatedText } from '../../utils'
import cloneDeep from 'lodash/cloneDeep'

export default {
  components: {
    DialogFormWrapper
  },
  data: () => ({
    blockExplanation: '',
    blockType: '',
    mode: 'list', // list, create, edit
    editingBlockage: null
  }),
  computed: {
    ...mapState({
      dialog: state => state.layout.dialog,
      isProcessing: state => state.client.isProcessing
    }),
    isClientBlocked() {
      return this.dialog.data.action !== 'blockClient'
    },
    clientBlockages() {
      const { blockages, clientBlockages, orderClientBlockages, client } = this.dialog.item
      return clientBlockages || blockages || orderClientBlockages || client.blockages || []
    },
    clientId() {
      const { client, clientId, id } = this.dialog.item || {}
      return client?.id || clientId || id
    },
    clientName() {
      const { clientName, name, client } = this.dialog.item || {}
      return clientName || name || client?.name
    },
    allBlockages() {
      return getBlockageTypesOptions()
    },
    avaliableBlockages() {
      const clientBlockageValues = this.clientBlockages.map(blockage => blockage.blockageType)
      return this.allBlockages.filter(blockage => !clientBlockageValues.includes(blockage.value))
    },
    headers() {
      return [
        { text: 'Id', value: 'id' },
        { text: 'Typ', value: 'blockageType' },
        { text: 'Komentarz', value: 'explanation', sortable: false },
        { text: 'Data', value: 'blockedAt' },
        { text: 'Autor', value: 'blockingUser.fullName' },
        { text: 'Akcja', value: 'action', sortable: false }
      ]
    }
  },
  beforeDestroy() {
    this.setClientProcessing(false)
  },
  methods: {
    dateStringFormat,
    getBlockageTypeWithIcon,
    getTruncatedText,
    ...mapActions({
      deleteClientBlockage: 'client/deleteClientBlockage',
      updateClientBlockage: 'client/updateClientBlockage',
      createClientBlockage: 'client/createClientBlockage',
      setClientProcessing: 'client/setClientProcessing'
    }),
    getBLockingUser(blockage) {
      const { blockingUser, blockingUserFullName } = blockage || {}
      return (blockingUser && blockingUser.fullName) || blockingUserFullName || '-'
    },
    clearForm() {
      this.blockExplanation = ''
      this.blockType = ''
      this.editingBlockage = null
      this.mode = 'list'
    },
    setEditingBlockage(blockage) {
      this.mode = 'edit'
      this.editingBlockage = cloneDeep(blockage)
    },
    setCreateBlockage(value = true) {
      this.mode = value ? 'create' : 'list'
    },
    deleteBlockage(blockageId) {
      this.deleteClientBlockage(blockageId).then(() => {
        this.clearForm()
        this.setCreateBlockage(false)
        const { blockages, clientBlockages, orderClientBlockages, client } = this.dialog.item
        if (blockages) {
          this.dialog.item.blockages = blockages.filter(blockage => blockage.id !== blockageId)
        }
        if (clientBlockages) {
          this.dialog.item.clientBlockages = clientBlockages.filter(blockage => blockage.id !== blockageId)
        }
        if (orderClientBlockages) {
          this.dialog.item.orderClientBlockages = orderClientBlockages.filter(blockage => blockage.id !== blockageId)
        }
        if (client?.blockages) {
          this.dialog.item.client.blockages = client.blockages.filter(blockage => blockage.id !== blockageId)
        }
      })
    },
    createBlockage() {
      this.createClientBlockage({
        id: this.clientId,
        blockExplanation: this.blockExplanation || null,
        blockType: this.blockType || null
      }).then((blockage) => {
        this.clearForm()
        this.setCreateBlockage(false)
        const { blockages, clientBlockages, orderClientBlockages, client } = this.dialog.item
        if (blockages) {
          this.dialog.item.blockages = [...blockages, blockage]
        } else if (clientBlockages) {
          this.dialog.item.clientBlockages = [...clientBlockages, blockage]
        } else if (orderClientBlockages) {
          this.dialog.item.orderClientBlockages = [...orderClientBlockages, blockage]
        } else if (client?.blockages) {
          this.dialog.item.client = { ...client, blockages: [...client.blockages, blockage] }
        }
        console.log(this.dialog.item)
      })
      this.blockExplanation = ''
    },
    editBlockage() {
      this.updateClientBlockage({
        id: this.editingBlockage.id,
        blockExplanation: this.editingBlockage.explanation || null,
        blockType: this.editingBlockage.blockageType || null
      }).then(() => {
        const updatedBlockage = { ...this.editingBlockage, explanation: this.editingBlockage.explanation || null }
        this.clearForm()
        this.setCreateBlockage(false)
        const { blockages, clientBlockages, orderClientBlockages, client } = this.dialog.item
        const getIndex = (blockages) => blockages.findIndex(blockage => blockage.id === updatedBlockage.id)
        if (blockages) {
          const index = getIndex(blockages)
          blockages[index] = updatedBlockage
        } else if (clientBlockages) {
          const index = getIndex(clientBlockages)
          clientBlockages[index] = updatedBlockage
        } else if (orderClientBlockages) {
          const index = getIndex(orderClientBlockages)
          orderClientBlockages[index] = updatedBlockage
        } else if (client?.blockages) {
          const index = getIndex(client.blockages)
          client.blockages[index] = updatedBlockage
        }
      })
    }
  }
}
</script>

<style lang="scss" scoped>
::v-deep th {
  background: none !important;
}
::v-deep tr {
  background-color: transparent !important;
}
::v-deep thead {
  background-color: #f1f1f1 !important;
}
::v-deep .v-data-table__wrapper {
  border: 1px solid #cfcfcf !important;
  border-bottom-left-radius: 6px !important;
  border-bottom-right-radius: 6px !important;
}
</style>
